//import { autocomplete } from '@algolia/autocomplete-js'
import { Link } from 'gatsby'
import { useState } from 'react'
import { createAutocomplete } from '@algolia/autocomplete-core'
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia'
import React from 'react'
import { client } from '../../helpers/AlgoliaClient'
import { IconSearchDL } from '../Icons'
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions'
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'
import CategoryImage from '../CategoryImage'
import { ProductTypeHelper } from '../../helpers/ProductTypeHelper'
import PropTypes from 'prop-types'

function debouncePromise(fn, time) {
  let timerId = undefined

  return function debounced(...args) {
    if (timerId) {
      clearTimeout(timerId)
    }

    return new Promise(resolve => {
      timerId = setTimeout(() => resolve(fn(...args)), time)
    })
  }
}

const searchClient = client()
const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
  key: 'search',
  search({ query, items, limit }) {
    const highlight = ({ item, query }) => {
      return {
        ...item,
        _highlightResult: {
          label: {
            value: query
              ? item.label.replace(new RegExp(query, 'gi'), `<mark>${query}</mark>`)
              : item.label,
          },
        },
      }
    }
    if (!query) {
      return items.slice(0, limit).map(item => highlight({ item, query }))
    }
    //
    return items
      .filter(item => item?.label?.toLowerCase().includes((query || '').toLowerCase()))
      .slice(0, limit)
      .map(item => highlight({ item, query }))
  },
  transformSource({ source }) {
    return {
      ...source,
      onSelect({ item }) {
        // Assuming the `setSearchState` function updates the search page state.
        // navigate(`/search?q=${item.label}`)
        window.location.href = '/search#search=' + item.label
      },
    }
  },
})
const querySuggestionsPlugin = createQuerySuggestionsPlugin({
  searchClient,
  indexName: 'products_query_suggestions',
  getSearchParams() {
    return {
      hitsPerPage: 5,
      highlightPreTag: '<mark>',
      highlightPostTag: '</mark>',
    }
  },

  transformSource({ source }) {
    return {
      ...source,
      onSelect({ item }) {
        // Assuming the `setSearchState` function updates the search page state.
        // navigate(`/search?q=${item.query}`)
        window.location.href = '/search#search=' + item.query
      },
    }
  },
})

function uniqBy(predicate) {
  return function runUniqBy(...rawSources) {
    const sources = rawSources.flat().filter(Boolean)
    const seen = new Set()

    return sources.map(source => {
      const items = source.getItems().filter(item => {
        const appliedItem = predicate({ source, item })
        const hasSeen = seen.has(appliedItem)

        seen.add(appliedItem)

        return !hasSeen
      })

      return {
        ...source,
        getItems() {
          return items
        },
      }
    })
  }
}
const removeDuplicates = uniqBy(({ source, item }) =>
  source.sourceId === 'querySuggestionsPlugin' ? item.query : item.label
)

export function Autocomplete({ dropDown, openSearchBar }) {
  const MIN_LENGHT = 3
  // const containerRef = useRef(null)
  const inputRef = React.useRef(null)
  const debounced = debouncePromise(items => Promise.resolve(items), 300)

  const [autocompleteState, setAutocompleteState] = React.useState({})
  const autocomplete = React.useMemo(
    () =>
      createAutocomplete({
        id: 'grayandsons-search',
        enterKeyHint: 'search',
        minLength: 3,
        plugins: [recentSearchesPlugin, querySuggestionsPlugin],
        onSubmit({ state }) {
          // (1) Navigate to the search page with the query.
          // navigate(`/search?q=${state.query}`)
          window.location.href = '/search#search=' + state.query
        },
        reshape({ sourcesBySourceId }) {
          const { recentSearchesPlugin, querySuggestionsPlugin, ...rest } = sourcesBySourceId

          return [
            removeDuplicates(recentSearchesPlugin, querySuggestionsPlugin),
            Object.values(rest),
          ]
        },
        onStateChange({ state }) {
          setAutocompleteState(state)
        },
        getSources({ setContext }) {
          return debounced([
            // (3) Use an Algolia index source.
            // {
            //   sourceId: 'querySuggestions',
            //   getItems({ query }) {
            //     if ( (query?.length || 0) < MIN_LENGHT) return []
            //     return getAlgoliaResults({
            //       searchClient,
            //       queries: [
            //         {
            //           indexName: 'products_query_suggestions',
            //           query,
            //           params: {
            //             hitsPerPage: 5,
            //             highlightPreTag: '<mark>',
            //             highlightPostTag: '</mark>',
            //           },
            //         },
            //       ],
            //     })
            //   },
            //   getItemInputValue({ item }) {
            //     return item.query
            //   },
            //   onSelect: function (event) {
            //     if (window) {
            //       window.location.href = '/search?q=' + event.item.query
            //     }
            //   },
            // },
            {
              sourceId: 'products',
              name: 'Products',
              getItemInputValue({ item }) {
                return item.name
              },
              getItems({ query }) {
                if ((query?.length || 0) < MIN_LENGHT) return []
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: 'products',
                      query,
                      facetFilters: ['qty:1'],
                      params: {
                        hitsPerPage: 5,
                        highlightPreTag: '<mark>',
                        highlightPostTag: '</mark>',
                      },
                    },
                  ],
                  transformResponse({ results, hits }) {
                    setContext({
                      nbProducts: results[0].nbHits,
                    })

                    // You can now use `state.context.nbProducts`
                    // anywhere where you have access to `state`.

                    return hits
                  },
                })
              },
              onSelect: function (event) {
                event.setQuery(event.item.name)
                // console.log(event.item)
                if (window) {
                  window.location.href = event.item.urlPath
                }
              },
              getItemUrl({ item }) {
                return item.urlPath
              },
            },
          ])
        },
      }),
    []
  )

  const [searchIsFocused, setSearchIsFocused] = useState(false)

  // Function to update state to true when input is focused
  const handleFocus = () => setSearchIsFocused(true)

  // Function to update state to false when focus is lost
  const handleBlur = () => setSearchIsFocused(false)

  return (
    <div {...autocomplete.getRootProps({})}>
      <label id="grayandsons-search-label" className="sr-only">
        Search
      </label>
      <form
        action={'/search'}
        id="desktop-search-form"
        className={'w-full grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 group'}
        {...autocomplete.getFormProps({ inputElement: inputRef.current })}
        onSubmit={event => {
          event.preventDefault()
          if (typeof window === 'undefined') return
          // console.log('searching', inputRef.current)
          // console.log('searching', autocompleteState)
          window.location.href = `/search/#search=${autocompleteState.query}`
        }}
      >
        <input
          {...autocomplete.getInputProps({})}
          onFocus={handleFocus}
          onBlur={handleBlur}
          name={'q'}
          aria-label="Search"
          className="search-input-style hidden mx-3 w-full border-b-[1px] bg-white group focus:border-red-700 group-hover:border-red-700 md:block"
          placeholder={'Search Gray and Sons'}
        />

        <button
          aria-label="Search"
          className={
            (dropDown === 3 ? 'bg-red-700  text-white ' : 'text-black ') +
            'overflow-hidden rounded-full ml-2 w-10 p-2 duration-200 group-hover:bg-red-700 group-hover:text-white'
          }
          onClick={openSearchBar}
        >
          <span className="sr-only">Search</span>
          <div className="icon-search h-6">
            <IconSearchDL />
          </div>
        </button>
      </form>
      {searchIsFocused && (
        <div
          className={
            'rounded-s drop-shadow-lg left-0 right-0 mx-auto absolute min-h-content w-full bg-white p-4 mt-2 border z-[200] duration-300 ' +
            (autocompleteState.isOpen ? 'opacity-100' : 'opacity-0 hidden')
          }
          {...autocomplete.getPanelProps({})}
        >
          <div className="max-w-[1366px] mx-auto">
            {autocompleteState.isOpen && (
              <div className="flex flex-row justify-between gap-4 min-w-[4rem]">
                <div className="flex-shrink">
                  <QuerySuggestions
                    title="Recent Searches"
                    autocomplete={autocomplete}
                    collection={autocompleteState.collections.find(
                      q => q.source.sourceId === 'recentSearchesPlugin'
                    )}
                  />
                  <QuerySuggestions
                    title="Query Suggestions"
                    autocomplete={autocomplete}
                    collection={autocompleteState.collections.find(
                      q => q.source.sourceId === 'querySuggestionsPlugin'
                    )}
                  />
                </div>
                <div className="flex-grow">
                  <ProductSuggestions
                    title="Products"
                    autocomplete={autocomplete}
                    context={autocompleteState.context}
                    collection={autocompleteState.collections.find(
                      q => q.source.sourceId === 'products'
                    )}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

Autocomplete.propTypes = {
  dropDown: PropTypes.number,
  openSearchBar: PropTypes.func,
}

const QuerySuggestions = ({ collection, autocomplete, title }) => {
  if (!collection) return null
  const { source, items } = collection
  if (!items?.length) return null
  return (
    <div className="aa-Source">
      {title && <h2 className="text-lg font-bold">{title}</h2>}
      {items.length > 0 && (
        <ul {...autocomplete.getListProps()}>
          {items.map(hit => (
            <li
              key={hit.objectID || hit.query || hit.label}
              className="aria-selected:text-red-700 p-2 cursor-pointer group"
              {...autocomplete.getItemProps({
                item: hit,
                source,
              })}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    hit?._highlightResult?.name?.value ||
                    hit?._highlightResult?.query?.value ||
                    hit?._highlightResult?.label?.value,
                }}
              ></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
QuerySuggestions.propTypes = {
  collection: PropTypes.object,
  autocomplete: PropTypes.object,
  title: PropTypes.string,
}

const ProductSuggestions = ({ collection, autocomplete, title, context }) => {
  if (!collection) return null
  const { source, items } = collection
  return (
    <div className="aa-Source xl:grid grid-cols-[auto_1fr] gap-5">
      {items[0]?.image && (
        <Link
          to={items[0].urlPath}
          className="hidden w-full max-w-60 xl:flex flex-col justify-around items-center border border-gray-300 hover:border-red-600 shadow-sm rounded-lg text-sm text-gray-400 hover:text-red-600 duration-200 overflow-hidden p-2 group"
        >
          <CategoryImage
            sku={items[0].sku}
            alt={items[0].name}
            title={items[0].title}
            normal={items[0].small_image}
            productType={items[0].productType || ProductTypeHelper(items[0])}
            tags={false}
            product={items[0]}
            imgStyle={{ objectFit: 'contain' }}
            className={'w-full h-full aspect-square rounded-lg '}
            sizes="2.5em"
            loading="eager"
          />
          Click for Details
        </Link>
      )}
      <div className="">
        {title && <h2 className="text-lg font-bold">{title}</h2>}
        {items.length > 0 && (
          <ul className="" {...autocomplete.getListProps()}>
            {/* <ul className="grid my-2 grid-flow-row grid-cols-8" {...autocomplete.getListProps()}> */}
            {/* {items.map(hit => ( */}
            {/*   <ProductGridCard key={hit.sku} hit={hit} url={hit.urlPath} tags={false} /> */}
            {/* ))} */}
            {items.map(hit => (
              <li
                key={hit.objectID}
                className="aa-Item aria-selected:text-red-700 cursor-pointer duration-200 	"
                {...autocomplete.getItemProps({
                  item: hit,
                  source,
                })}
              >
                <div className="aa-ItemWrapper">
                  <div className="aa-ItemContent flex flex-row gap-4 my-2 items-center">
                    {hit.image && (
                      <div className="size-10">
                        <CategoryImage
                          sku={hit.sku}
                          alt={hit.name}
                          title={hit.title}
                          normal={hit.small_image}
                          productType={hit.productType || ProductTypeHelper(hit)}
                          tags={false}
                          product={hit}
                          imgStyle={{ objectFit: 'contain' }}
                          className={'w-full h-full aspect-square '}
                          sizes="2.5em"
                          loading="eager"
                        />
                      </div>
                    )}
                    <div
                      className="max-w-[42rem] text-ellipsis line-clamp-1"
                      dangerouslySetInnerHTML={{ __html: hit._highlightResult.title.value }}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        <b>{context.nbProducts} </b>matching products
      </div>
    </div>
  )
}

ProductSuggestions.propTypes = {
  collection: PropTypes.object,
  autocomplete: PropTypes.object,
  title: PropTypes.string,
  context: PropTypes.shape({
    nbProducts: PropTypes.number,
  }),
}
